"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "executeRemotely", {
    enumerable: true,
    get: function() {
        return executeRemotely;
    }
});
const _path = /*#__PURE__*/ _interop_require_default(require("path"));
const _logger = /*#__PURE__*/ _interop_require_default(require("@lage-run/logger"));
const _initializeReporters = require("../initializeReporters.js");
const _rpc = require("@lage-run/rpc");
const _filterArgsForTasks = require("../run/filterArgsForTasks.js");
const _simulateFileAccess = require("./simulateFileAccess.js");
const _parseServerOption = require("../parseServerOption.js");
const _config = require("@lage-run/config");
const _workspacetools = require("workspace-tools");
const _launchServerInBackground = require("../launchServerInBackground.js");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
async function tryCreateClient(host, port) {
    const client = (0, _rpc.createClient)({
        baseUrl: `http://${host}:${port}`,
        httpVersion: "2"
    });
    try {
        const success = await client.ping({});
        if (success.pong) {
            return client;
        }
    } catch (e) {
        if (e instanceof _rpc.ConnectError) {
            return undefined;
        }
        throw e;
    }
    return undefined;
}
async function tryCreateClientWithRetries(host, port, logger) {
    let client;
    const start = Date.now();
    while(Date.now() - start < 5 * 1000){
        try {
            client = await tryCreateClient(host, port);
            if (client) {
                return client;
            }
        } catch (e) {
            if (e instanceof _rpc.ConnectError) {
                logger.error("Error connecting to server", e);
            }
        }
        await new Promise((resolve)=>setTimeout(resolve, 1000));
    }
    return undefined;
}
async function executeOnServer(args, client, logger) {
    const task = args.length === 1 ? args[0] : args[1];
    const packageName = args.length > 1 ? args[0] : undefined;
    if (!task) {
        throw new Error("No task provided");
    }
    const { taskArgs } = (0, _filterArgsForTasks.filterArgsForTasks)(args ?? []);
    try {
        const response = await client.runTarget({
            packageName,
            task,
            taskArgs
        });
        logger.info(`Task ${response.packageName} ${response.task} exited with code ${response.exitCode}`);
        return response;
    } catch (error) {
        if (error instanceof _rpc.ConnectError) {
            logger.error("Error connecting to server", {
                error
            });
        } else {
            logger.error("Error running task", {
                error
            });
        }
    }
}
async function executeRemotely(options, command) {
    // launch a 'lage-server.js' process, detached if it is not already running
    // send the command to the server process
    const { server, tasks, nodeArg } = options;
    const timeout = options.timeout ?? 5 * 60;
    const { host, port } = (0, _parseServerOption.parseServerOption)(server);
    const cwd = options.cwd ?? process.cwd();
    const config = await (0, _config.getConfig)(cwd);
    const logger = (0, _logger.default)();
    options.logLevel = options.logLevel ?? "info";
    options.reporter = options.reporter ?? "json";
    await (0, _initializeReporters.initializeReporters)(logger, options, config.reporters);
    const root = (0, _workspacetools.getWorkspaceRoot)(options.cwd ?? process.cwd());
    let client = await tryCreateClient(host, port);
    const args = command.args;
    logger.info(`Command args ${command.args.join(" ")}`);
    if (!client) {
        await (0, _launchServerInBackground.launchServerInBackground)({
            host,
            port,
            tasks,
            args,
            timeout,
            logger,
            root,
            nodeArg
        });
        logger.info("Creating a client to connect to the background services");
        client = await tryCreateClientWithRetries(host, port, logger);
        if (!client) {
            throw new Error("Server could not be started");
        }
    }
    logger.info(`Executing on server http://${host}:${port}`);
    const response = await executeOnServer(args, client, logger);
    if (response) {
        process.stdout.write(response.stdout);
        process.stderr.write(response.stderr);
        process.exitCode = response.exitCode;
        // we will simulate file access even if exit code may be non-zero
        const relativeGlobalInputsForTarget = _path.default.relative(root, _path.default.join(response.cwd, response.globalInputHashFile));
        await (0, _simulateFileAccess.simulateFileAccess)(logger, root, [
            ...response.inputs,
            relativeGlobalInputsForTarget
        ], response.outputs);
    } else {
        process.exitCode = 1;
    }
    logger.info("Task execution finished");
}
